// Staff radio client: push-to-talk over the site's radio socket (apps/web/worker/api/radio-routes.ts).
// Half duplex like a walkie-talkie. The microphone is opened only while the button is held: it
// opens on the press, in parallel with the floor request, and what it hears before the floor is
// granted is sent once it is, so the first words are never lost.
//
// This is the web engine (the console, and any browser). The Android apps swap in a native engine
// with the same interface (platform.radioEngine) that keeps the radio on in the background.
import { encode, decode, resample } from "./mulaw.ts";

export const RATE = 16_000;
const FRAME = 320; // 20 ms at 16 kHz
/** At most this much audio waits for the floor (1 s); older frames are dropped. */
const MAX_PENDING = 50;
const VERSION = 1;

export type RadioState =
  | { kind: "off" }
  | { kind: "connecting" }
  | { kind: "ready" }
  | { kind: "listening"; name: string }
  | { kind: "requesting" }
  | { kind: "talking"; until: number }
  | { kind: "busy"; name: string }
  | { kind: "error"; message: string };

export interface RadioOptions {
  /** A one-use ticket for the site's radio socket, and with `pass` also a reusable pass (one shift). */
  getTicket: (pass: boolean) => Promise<{ ticket: string; pass?: string }>;
  /** wss://…/api/v1/sites/<site>/radio, without a query. */
  wsBase: string;
  onState: (s: RadioState) => void;
}

/** What the push-to-talk button drives: this web engine, or the Android app's native service. */
export interface RadioEngine {
  readonly current: RadioState;
  /** Keeps working with the app in the background; Volume Up talks even then. */
  readonly background: boolean;
  speaker: boolean;
  /** Must run from a user gesture. */
  start(): Promise<void>;
  stop(): void;
  press(): void;
  releaseButton(): void;
  /** Detaches the screen from the engine without turning the radio off (native engines). */
  dispose(): void;
}

export class Radio implements RadioEngine {
  readonly background = false;
  private pending: Uint8Array[] = [];
  private o: RadioOptions;
  private ws: WebSocket | null = null;
  private ctx: AudioContext | null = null;
  private playAt = 0;
  private mic: { stream: MediaStream; node: ScriptProcessorNode; src: MediaStreamAudioSourceNode; buf: Float32Array; fill: number } | null = null;
  private state: RadioState = { kind: "off" };
  private stopped = false;
  private retry: ReturnType<typeof setTimeout> | undefined;
  private attempt = 0;
  private holding = false;
  speaker = true;

  constructor(o: RadioOptions) { this.o = o; }

  private set(s: RadioState) { this.state = s; this.o.onState(s); }
  get current() { return this.state; }

  /** Must run from a user gesture: it unlocks audio playback. */
  async start() {
    this.stopped = false;
    this.ctx ??= new AudioContext();
    if (this.ctx.state === "suspended") {
      // Started without a tap (the door tablet's switch): playback unlocks on the next touch.
      const ctx = this.ctx;
      ctx.resume().catch(() => undefined);
      if (typeof window !== "undefined") window.addEventListener("pointerdown", () => void ctx.resume().catch(() => undefined), { once: true });
    }
    this.connect();
  }

  stop() {
    this.stopped = true;
    clearTimeout(this.retry);
    this.release();
    this.ws?.close();
    this.ws = null;
    this.set({ kind: "off" });
  }

  private async connect() {
    if (this.stopped) return;
    this.set({ kind: "connecting" });
    try {
      const ws = new WebSocket(`${this.o.wsBase}?ticket=${encodeURIComponent((await this.o.getTicket(false)).ticket)}`);
      ws.binaryType = "arraybuffer";
      this.ws = ws;
      ws.onopen = () => { this.attempt = 0; };
      ws.onmessage = (m) => this.onMessage(m.data);
      ws.onclose = () => {
        this.release();
        if (this.stopped) return;
        this.set({ kind: "connecting" });
        this.retry = setTimeout(() => this.connect(), Math.min(30_000, 1_000 * 2 ** this.attempt++));
      };
    } catch (e) {
      this.set({ kind: "error", message: "Radio unavailable. Retrying." });
      this.retry = setTimeout(() => this.connect(), Math.min(30_000, 1_000 * 2 ** this.attempt++));
    }
  }

  private onMessage(data: unknown) {
    if (data instanceof ArrayBuffer) { this.play(new Uint8Array(data)); return; }
    if (data === "pong") return;
    const m = JSON.parse(String(data)) as { t: string; name?: string; talking?: { name: string } | null; max_ms?: number };
    switch (m.t) {
      case "hello": this.set(m.talking ? { kind: "listening", name: m.talking.name } : { kind: "ready" }); break;
      case "talking": this.chirp(880, 0.08); this.set({ kind: "listening", name: m.name ?? "Someone" }); break;
      case "clear":
        if (this.state.kind === "talking") this.release();
        this.chirp(660, 0.12);
        this.set({ kind: "ready" });
        break;
      case "busy": this.holding = false; this.release(); this.set({ kind: "busy", name: m.name ?? "Someone" }); setTimeout(() => { if (this.state.kind === "busy") this.set({ kind: "ready" }); }, 1500); break;
      case "granted":
        if (!this.holding) { this.send({ t: "over" }); break; }
        this.chirp(1320, 0.06);
        this.set({ kind: "talking", until: Date.now() + (m.max_ms ?? 30_000) });
        for (const f of this.pending.splice(0)) this.ws?.send(f);
        break;
    }
  }

  private send(m: unknown) { if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m)); }

  /** Button pressed. */
  press() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || this.state.kind === "listening") return;
    this.holding = true;
    this.pending = [];
    this.set({ kind: "requesting" });
    this.send({ t: "talk" });
    void this.openMic();
  }

  dispose() { this.stop(); }

  /** Button released. */
  releaseButton() {
    this.holding = false;
    if (this.state.kind === "talking" || this.state.kind === "requesting") { this.release(); this.send({ t: "over" }); }
  }

  private async openMic() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 } });
      if (!this.holding || !this.ctx) { stream.getTracks().forEach((t) => t.stop()); return; }
      const src = this.ctx.createMediaStreamSource(stream);
      // ScriptProcessorNode is deprecated but runs in every WebView; an AudioWorklet module would
      // need script-src for a worklet file in each app's CSP. 1024 samples at 48 kHz is ~21 ms.
      const node = this.ctx.createScriptProcessor(1024, 1, 1);
      const rate = this.ctx.sampleRate;
      this.mic = { stream, node, src, buf: new Float32Array(FRAME), fill: 0 };
      node.onaudioprocess = (ev) => {
        const mic = this.mic;
        if (!mic) return;
        const pcm = resample(ev.inputBuffer.getChannelData(0), rate, RATE);
        for (let i = 0; i < pcm.length; i++) {
          mic.buf[mic.fill++] = pcm[i]!;
          if (mic.fill === FRAME) {
            const out = new Uint8Array(FRAME + 1);
            out[0] = VERSION;
            out.set(encode(mic.buf), 1);
            if (this.state.kind === "talking") { if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(out); }
            else if (this.pending.push(out) > MAX_PENDING) this.pending.shift();
            mic.fill = 0;
          }
        }
        if (this.state.kind === "talking" && Date.now() > this.state.until) this.releaseButton();
      };
      src.connect(node);
      node.connect(this.ctx.destination); // required for onaudioprocess to fire; output stays silent
    } catch {
      this.holding = false;
      this.send({ t: "over" });
      this.set({ kind: "error", message: "Allow the microphone to talk." });
    }
  }

  private release() {
    this.pending = [];
    const mic = this.mic;
    this.mic = null;
    if (!mic) return;
    mic.node.onaudioprocess = null;
    try { mic.src.disconnect(); mic.node.disconnect(); } catch { /* already */ }
    mic.stream.getTracks().forEach((t) => t.stop());
  }

  /** Plays one frame; an 80 ms jitter buffer absorbs network wobble. */
  private play(frame: Uint8Array) {
    if (!this.ctx || !this.speaker || frame[0] !== VERSION) return;
    const samples = decode(frame.subarray(1));
    const buf = this.ctx.createBuffer(1, samples.length, RATE);
    buf.copyToChannel(samples, 0);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.ctx.destination);
    const now = this.ctx.currentTime;
    if (this.playAt < now || this.playAt > now + 1) this.playAt = now + 0.08;
    src.start(this.playAt);
    this.playAt += buf.duration;
  }

  private chirp(hz: number, seconds: number) {
    if (!this.ctx || !this.speaker) return;
    const o = this.ctx.createOscillator(), g = this.ctx.createGain();
    o.frequency.value = hz;
    g.gain.value = 0.08;
    o.connect(g).connect(this.ctx.destination);
    o.start();
    o.stop(this.ctx.currentTime + seconds);
  }
}
